import React, { PureComponent, Fragment } from 'react'
import { reduxForm, formValues } from 'redux-form'
import PropTypes from 'prop-types'
import track from 'react-tracking'
import ReactMarkdown from 'markdown-to-jsx'

import { RadioButtonGroup, RadioButton } from '@ynap/radio-button'
import Notification from '@ynap/notification'
import Checkbox from '@ynap/checkbox'
import BEMHelper from '@ynap/bem-helper'
import Field from '@ynap/field'
import { Button, SimpleAsyncButton } from '@ynap/button'
import { onSubmitFail, submitAndCollateErrorsPromise } from '@ynap/form-utils'
import { OptimizelyFeature } from '@ynap/optimizely-utils'

import collateErrors from './collateErrors'
import validate from './validate'
import { trackConfirmReturn } from './analytics'
import { trackGuestOrderTrackError } from '../GuestOrders/analytics'

const bem = new BEMHelper('RefundForm')

@track({
    category: { primaryCategory: 'ecommerce', subCategory: 'my orders' },
})
export class RefundForm extends PureComponent {
    static propTypes = {
        form: PropTypes.string.isRequired,
        error: PropTypes.string,
        submitting: PropTypes.bool,
        handleSubmit: PropTypes.func,
        messages: PropTypes.shape({
            refundForm: PropTypes.shape({
                refundMethodTitle: PropTypes.func.isRequired,
                refundAsCharged: PropTypes.func.isRequired,
                termsAndConditions: PropTypes.func.isRequired,
                conditions: PropTypes.func.isRequired,
                conditionsTitle: PropTypes.func.isRequired,
                submit: PropTypes.func.isRequired,
                creditCard: PropTypes.func.isRequired,
            }).isRequired,
        }).isRequired,
        includesReturn: PropTypes.bool,
        className: PropTypes.string,
        children: PropTypes.node,
        seeMoreLink: PropTypes.string.isRequired,
        termsAndConditions: PropTypes.bool.isRequired,
        helpPage: PropTypes.string,
        refundPaymentMethods: PropTypes.array.isRequired,
        country: PropTypes.string,
        guestUser: PropTypes.bool,
        storeCreditExclusionList: PropTypes.array,
        storeCreditReturnsExclusionList: PropTypes.array,
    }

    static defaultProps = {
        component: 'form',
        submitting: false,
    }

    constructor() {
        super()
        this.submit = this.submit.bind(this)
        this.fetchRates = this.fetchRates.bind(this)
        this.state = {}
    }

    componentDidUpdate(prevProps) {
        const { error, tracking, guestUser } = this.props

        if (guestUser && !prevProps.error && error) {
            tracking.trackEvent(trackGuestOrderTrackError('create return', error))
        }
    }

    submit(values) {
        const { createReturn, returnOrder, country, email, guestUser } = this.props
        const createReturnWithEmail = guestUser ? { ...returnOrder, email } : returnOrder
        const { refundPaymentMethod, bankName, accountName, swiftCode, accountNo, bankCode, sortCode, branchName } =
            values

        return submitAndCollateErrorsPromise(
            createReturn(
                {
                    refundPaymentMethod,
                    bankName,
                    accountName,
                    swiftCode,
                    accountNo,
                    bankCode,
                    sortCode,
                    branchName,
                    ...createReturnWithEmail,
                },
                country
            ),
            values,
            this.props
        )
    }

    @track(({ refundPaymentMethod, returnItems, returnOrder, locale }) =>
        trackConfirmReturn(refundPaymentMethod, returnItems, returnOrder.orderNumber, locale)
    )
    fetchRates(currency) {
        if (this.props.config?.head?.currencyConversionRates?.serviceUrl) {
            const url = `${this.props.config.head.currencyConversionRates.serviceUrl}${currency}.json`

            this.setState({
                ...this.state,
                [currency]: {},
            })

            fetch(url)
                .then((res) => res?.json())
                .then((json) => {
                    this.setState({
                        ...this.state,
                        [currency]: json.rates,
                    })
                })
                .catch()
        }
    }

    render() {
        const {
            component: Component,
            messages,
            children,
            className,
            form,
            handleSubmit,
            submitting,
            termsAndConditions,
            includesReturn,
            error,
            helpPage,
            paymentMethod,
            refundPaymentMethods,
            locale,
            storeCreditExclusionList,
            storeCreditReturnsExclusionList,
            country,
        } = this.props
        const { cod: codMessages, refundForm: refundFormMessages } = messages

        const cardOnly = paymentMethod?.length === 1 && paymentMethod?.find((method) => method.type === 'CREDIT_CARD')
        const cod = paymentMethod?.find((method) => method.type === 'COD')

        const useStoreCreditOpt = refundPaymentMethods?.includes('STORE_CREDIT')

        return (
            <OptimizelyFeature
                feature={[
                    'web00126_store_credit_refund_option_at_top_account_web',
                    'web00041_store_credit_ui_enhancement_account_responsive',
                    'web00362_store_credit_ui_enhancement_account_responsive',
                ]}
                bypassExperiment={false}
                autoUpdate={true}
                disableDecisionEvent={!useStoreCreditOpt}
            >
                {(props) => {
                    const { variables = {}, isEnabled, optimizely } = props
                    const { storeCreditOnTop } = variables
                    const showStoreCreditFirst = isEnabled && storeCreditOnTop

                    const onChange = (_, value) => {
                        optimizely?.track(
                            value === 'STORE_CREDIT' ? 'returnform_click_storecredit' : 'returnform_click_other_refund'
                        )
                    }

                    const currency = this.props.returnItems?.[0]?.price?.currency?.label?.toLowerCase()
                    if (currency && currency !== 'eur' && !this.state?.[currency]) {
                        this.fetchRates(currency)
                    }

                    const onSubmit = () => {
                        if (this.props.refundPaymentMethod) {
                            const value = this.props.returnItems?.length ?? 1
                            const revenue = Math.round(
                                this.props.returnItems?.reduce((acc, item) => {
                                    const amount = item?.price?.value?.amount / item?.price?.value?.divisor
                                    const currency = item?.price?.currency?.label?.toLowerCase()
                                    const eurAmount = parseFloat(this.state?.[currency]?.EUR) * amount
                                    return Number.isNaN(eurAmount) ? acc + amount : acc + eurAmount
                                }, 0) * 100
                            )
                            if (this.props.refundPaymentMethod === 'STORE_CREDIT') {
                                optimizely?.track('returnform_submit_storecredit')
                                optimizely?.track('returnform_storecredit_revenue', { revenue })
                            } else {
                                optimizely?.track('returnform_submit_other_refund')
                                optimizely?.track('returnform_other_refund_revenue', { revenue })
                            }
                            optimizely?.track('returnform_units', { value })
                            optimizely?.track('returnform_revenue', { revenue })
                        }
                    }

                    return (
                        <Component
                            className={bem(null, { showStoreCreditFirst }, className)}
                            id={form}
                            onSubmit={handleSubmit ? handleSubmit(this.submit) : undefined}
                        >
                            {includesReturn && (
                                <Fragment>
                                    <div className={bem('refundPaymentMethodTitle')}>
                                        {refundFormMessages.refundMethodTitle()}
                                    </div>
                                    <Field
                                        className={bem('refundPaymentMethod', { showStoreCreditFirst })}
                                        component={RadioButtonGroup}
                                        name="refundPaymentMethod"
                                        errorStyle="none"
                                        onChange={onChange}
                                    >
                                        {!!showStoreCreditFirst &&
                                            refundPaymentMethods.includes('STORE_CREDIT') &&
                                            !(
                                                storeCreditExclusionList.includes(country) ||
                                                storeCreditReturnsExclusionList.includes(country)
                                            ) && (
                                                <RadioButton
                                                    value="STORE_CREDIT"
                                                    label={refundFormMessages.storeCredit()}
                                                    description={refundFormMessages.storeCreditInfo({
                                                        showStoreCreditFirst,
                                                    })}
                                                    className={bem('storeCredit', { showStoreCreditFirst })}
                                                    showError={true}
                                                >
                                                    {
                                                        <div className={bem('storeCreditBanner')}>
                                                            {refundFormMessages?.storeCreditBanner?.()}
                                                        </div>
                                                    }
                                                </RadioButton>
                                            )}
                                        {refundPaymentMethods.includes('ORIGINAL') &&
                                            (!cod ? (
                                                <RadioButton
                                                    value="ORIGINAL"
                                                    label={
                                                        cardOnly
                                                            ? refundFormMessages.refundCard()
                                                            : refundFormMessages.refundAsCharged()
                                                    }
                                                    description={
                                                        <ReactMarkdown>
                                                            {`${
                                                                cardOnly
                                                                    ? refundFormMessages.refundCardInfo({
                                                                          showStoreCreditFirst,
                                                                      })
                                                                    : refundFormMessages.refundAsChargedInfo({
                                                                          showStoreCreditFirst,
                                                                      })
                                                            }`}
                                                        </ReactMarkdown>
                                                    }
                                                    className={bem('refundRadio', { showStoreCreditFirst })}
                                                    showError={true}
                                                />
                                            ) : (
                                                <RadioButton
                                                    value="ORIGINAL"
                                                    label={'Bank Transfer'}
                                                    description={codMessages.refund()}
                                                    className={bem('refundRadio')}
                                                    showError={true}
                                                />
                                            ))}
                                        {cod && (
                                            <>
                                                {codMessages.bankCodeFieldName && (
                                                    <Field name="bankCode" label={codMessages.bankCodeFieldName()} />
                                                )}
                                                {codMessages.bankNameFieldName && (
                                                    <Field name="bankName" label={codMessages.bankNameFieldName()} />
                                                )}
                                                {codMessages.sortCodeFieldName && (
                                                    <Field name="sortCode" label={codMessages.sortCodeFieldName()} />
                                                )}
                                                {codMessages.branchNameFieldName && (
                                                    <Field
                                                        name="branchName"
                                                        label={codMessages.branchNameFieldName()}
                                                    />
                                                )}
                                                {codMessages.accountNameFieldName && (
                                                    <Field
                                                        name="accountName"
                                                        label={codMessages.accountNameFieldName()}
                                                    />
                                                )}
                                                {codMessages.accountNoFieldName && (
                                                    <Field name="accountNo" label={codMessages.accountNoFieldName()} />
                                                )}
                                                {codMessages.swiftCodeFieldName && (
                                                    <Field name="swiftCode" label={codMessages.swiftCodeFieldName()} />
                                                )}
                                            </>
                                        )}
                                        {!showStoreCreditFirst &&
                                            refundPaymentMethods.includes('STORE_CREDIT') &&
                                            !(
                                                storeCreditExclusionList.includes(country) ||
                                                storeCreditReturnsExclusionList.includes(country)
                                            ) && (
                                                <RadioButton
                                                    value="STORE_CREDIT"
                                                    label={refundFormMessages.storeCredit()}
                                                    description={refundFormMessages.storeCreditInfo({
                                                        showStoreCreditFirst,
                                                    })}
                                                    className={bem('storeCredit', { showStoreCreditFirst })}
                                                    showError={true}
                                                />
                                            )}
                                    </Field>
                                </Fragment>
                            )}
                            <div className={bem('align', { showStoreCreditFirst })}>
                                <Notification
                                    className={bem('conditions', { showStoreCreditFirst })}
                                    message={refundFormMessages.conditionsTitle()}
                                    type="info"
                                    emphasis="important"
                                >
                                    <div className={bem('conditionsMessage')}> {refundFormMessages.conditions()} </div>
                                    <Button
                                        component="a"
                                        onClick={() =>
                                            window.open(
                                                `/${locale.id.toLowerCase()}${helpPage}`,
                                                'popUpWindow',
                                                'height=600, width=650'
                                            )
                                        }
                                        type="primary-link"
                                        className={bem('seeMoreLink')}
                                    >
                                        {refundFormMessages.seeMore()}
                                    </Button>
                                </Notification>
                                <Field
                                    className={bem('termsAndConditions')}
                                    component={Checkbox}
                                    name="termsAndConditions"
                                    value={true}
                                >
                                    {refundFormMessages.termsAndConditions()}
                                </Field>
                            </div>
                            {handleSubmit && (
                                <SimpleAsyncButton
                                    containerClass={bem('submitWrapper')}
                                    className={bem('submit')}
                                    disabled={!termsAndConditions}
                                    copy={refundFormMessages.submit()}
                                    loading={submitting}
                                    onClick={onSubmit}
                                />
                            )}
                            {children}
                            {error && <Notification message={error} type="error" />}
                        </Component>
                    )
                }}
            </OptimizelyFeature>
        )
    }
}

const RefundFormWithValues = formValues({
    termsAndConditions: 'termsAndConditions',
    refundPaymentMethod: 'refundPaymentMethod',
})(RefundForm)
export default reduxForm({ form: 'refundForm', collateErrors, validate, onSubmitFail })(RefundFormWithValues)
